/**
 * Centralized error handling utilities for consistent error management across the application
 */

/**
 * Error severity levels
 */
export enum ErrorSeverity {
    /** Informational - logged but doesn't affect user experience */
    INFO = 'info',
    /** Warning - something unexpected but recoverable */
    WARNING = 'warning',
    /** Error - functionality impaired but app continues */
    ERROR = 'error',
    /** Critical - requires user action or app restart */
    CRITICAL = 'critical',
}

/**
 * Error categories for better organization and handling
 */
export enum ErrorCategory {
    /** Authentication/authorization errors */
    AUTH = 'auth',
    /** Network/API communication errors */
    NETWORK = 'network',
    /** Data validation errors */
    VALIDATION = 'validation',
    /** Encryption/decryption errors */
    SECURITY = 'security',
    /** Storage errors (localStorage, sessionStorage) */
    STORAGE = 'storage',
    /** UI/rendering errors */
    UI = 'ui',
    /** Configuration errors */
    CONFIG = 'config',
}

/**
 * Structured error information
 */
export interface AppError {
    /** Error category */
    category: ErrorCategory;
    /** Error severity */
    severity: ErrorSeverity;
    /** Human-readable error message */
    message: string;
    /** Original error object */
    originalError?: unknown;
    /** Additional context */
    context?: Record<string, unknown>;
    /** Timestamp */
    timestamp: Date;
}

/**
 * Logs an error with consistent formatting
 * Only logs in development mode unless severity is CRITICAL
 */
export const logError = (error: AppError): void => {
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Always log critical errors, others only in development
    if (!isDevelopment && error.severity !== ErrorSeverity.CRITICAL) {
        return;
    }

    const logFn = error.severity === ErrorSeverity.CRITICAL || error.severity === ErrorSeverity.ERROR
        ? console.error
        : error.severity === ErrorSeverity.WARNING
        ? console.warn
        : console.log;

    const prefix = `[${error.category.toUpperCase()}] ${error.severity.toUpperCase()}:`;

    logFn(prefix, error.message);

    if (error.context && Object.keys(error.context).length > 0) {
        logFn('Context:', error.context);
    }

    if (error.originalError) {
        logFn('Original error:', error.originalError);
    }
};

/**
 * Creates a structured error
 */
export const createError = (
    category: ErrorCategory,
    severity: ErrorSeverity,
    message: string,
    originalError?: unknown,
    context?: Record<string, unknown>
): AppError => {
    return {
        category,
        severity,
        message,
        originalError,
        context,
        timestamp: new Date(),
    };
};

/**
 * Wraps an async function with consistent error handling
 */
export const withErrorHandling = <T extends (...args: never[]) => Promise<unknown>>(
    fn: T,
    category: ErrorCategory,
    errorMessage: string
): T => {
    return (async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
        try {
            return await fn(...args) as Awaited<ReturnType<T>>;
        } catch (error) {
            const appError = createError(
                category,
                ErrorSeverity.ERROR,
                errorMessage,
                error
            );
            logError(appError);
            throw error; // Re-throw to maintain error propagation
        }
    }) as T;
};

/**
 * Handles errors for Redux Toolkit async thunks
 */
export const handleThunkError = (
    error: unknown,
    category: ErrorCategory,
    message: string,
    context?: Record<string, unknown>
): string => {
    const appError = createError(
        category,
        ErrorSeverity.ERROR,
        message,
        error,
        context
    );
    logError(appError);

    // Extract user-friendly message
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return message;
};

/**
 * Handles recoverable errors (e.g., corrupted storage data)
 */
export const handleRecoverableError = (
    error: unknown,
    category: ErrorCategory,
    message: string,
    recoveryAction: () => void,
    context?: Record<string, unknown>
): void => {
    const appError = createError(
        category,
        ErrorSeverity.WARNING,
        message,
        error,
        context
    );
    logError(appError);
    recoveryAction();
};

/**
 * Type guard to check if error is an Error object
 */
export const isError = (error: unknown): error is Error => {
    return error instanceof Error;
};

/**
 * Type guard to check if error has a response (axios-like error)
 */
export const hasResponse = (error: unknown): error is { response: { data: unknown } } => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as Record<string, unknown>).response === 'object'
    );
};

/**
 * Extracts a user-friendly message from various error types
 */
export const getErrorMessage = (error: unknown): string => {
    if (isError(error)) {
        return error.message;
    }
    if (hasResponse(error)) {
        const data = error.response.data;
        if (typeof data === 'string') {
            return data;
        }
        if (typeof data === 'object' && data !== null && 'message' in data) {
            return String((data as Record<string, unknown>).message);
        }
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'An unexpected error occurred';
};
